import _ from 'lodash';
import type { z } from 'zod';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { VideoAttributes } from './constants';
import { DBVideoSchema, VideoSchema } from './schemas';

type DBVideo = z.infer<typeof DBVideoSchema>;

const TOPIC_CODE_PARTS = ['subject', 'categoryCode', 'topicCode'] as const;

export const VideoCodecConfig: TransformerConfig<
  typeof VideoSchema,
  typeof DBVideoSchema,
  'topicId'
> = {
  name: 'Video',
  clientSchema: VideoSchema,
  dbSchema: DBVideoSchema,
  transformers: [
    createRenameTransformer<typeof VideoSchema, typeof DBVideoSchema>('id', 'videoId'),
    createRenameTransformer<typeof VideoSchema, typeof DBVideoSchema>('hostName', 'host'),

    // subject, categoryCode, topicCode ↔ topic: 'subject#category#topic'
    createTransformer<typeof VideoSchema, typeof DBVideoSchema>({
      encoder: {
        sourceFields: TOPIC_CODE_PARTS,
        dependencies: TOPIC_CODE_PARTS,
        encode: ({ subject, categoryCode, topicCode }) => ({
          subject,
          topic: [subject, categoryCode, topicCode].join('#'),
        }),
      },
      decoder: {
        sourceFields: ['topic'] as const,
        decode: ({ topic }) => {
          const parts = (topic as DBVideo['topic']).split('#');
          return { categoryCode: parts[1] as string, topicCode: parts[2] as string };
        },
      },
    }),

    // Handle search string updates
    createTransformer<typeof VideoSchema, typeof DBVideoSchema>({
      encoder: {
        sourceFields: VideoAttributes.Search,
        dependencies: VideoAttributes.Search,
        encode: video => {
          let searchStr = '';
          VideoAttributes.Search.forEach(attr => {
            const val = video[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(video, VideoAttributes.Search) };
        },
      },
    }),
  ],
  derived: {
    topicId: video =>
      buildRecordId('videoTopic', {
        subject: video.subject,
        categoryCode: video.categoryCode,
        code: video.topicCode,
      }),
  },
};

export const encodeVideoToDb = createEncodeToDbFn(VideoCodecConfig);
export const decodeVideoFromDb = createDecodeFromDbFn(VideoCodecConfig);
